<template>
    <b-container fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3">
                    <h3>벤더코드 관리</h3>
                    <p class="fw-400 text-secondary mb-0">평가에 사용되는 벤더코드를 조회하고 일괄 등록합니다.</p>
                </div>
            </b-col>
        </b-row>

        <!-- 검색 필터 -->
        <b-row class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">벤더코드 검색</h5>
                            <div v-if="listLoading" class="d-flex align-items-center" style="height: 36px">
                                <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                <p class="d-inline-block mb-0">조회중...</p>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <b-button form="vendor-code-search-form" type="reset" class="mr-2" :disabled="listLoading">
                                    초기화
                                </b-button>
                                <b-button variant="primary" :disabled="listLoading" form="vendor-code-search-form" type="submit">
                                    검색하기
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-form id="vendor-code-search-form" @submit.stop.prevent="onSearch" @reset="resetFilter">
                            <b-row>
                                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                                    <b-form-group label="프로젝트 코드" label-for="filter-project-code" class="mb-0">
                                        <b-input-group size="sm">
                                            <b-form-input id="filter-project-code" v-model="filter.project_code" placeholder="프로젝트 코드 입력"/>
                                            <b-input-group-append>
                                                <b-button variant="outline-secondary" title="프로젝트 코드 검색" @click="openProjectCodeLookup">
                                                    <fa icon="search"/>
                                                </b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                                    <b-form-group label="업체명" label-for="filter-company-name" class="mb-0">
                                        <b-form-input id="filter-company-name" v-model="filter.company_name" placeholder="업체명 입력" size="sm"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                                    <b-form-group label="벤더코드" label-for="filter-vendor-code" class="mb-0">
                                        <b-form-input id="filter-vendor-code" v-model="filter.vendor_code" placeholder="벤더코드 입력" size="sm"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="3">
                                    <b-form-group label="검증 여부" label-for="filter-validated" class="mb-0">
                                        <b-form-select id="filter-validated" v-model="filter.validated" :options="validatedOptions" size="sm"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-form>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- 목록 테이블 -->
        <b-row>
            <b-col cols="12">
                <b-card no-body>
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">
                                벤더코드 목록
                                <b-badge variant="secondary" class="ml-1">{{ totalRows }}</b-badge>
                            </h5>
                            <div class="d-flex align-items-center">
                                <b-button size="sm" variant="outline-secondary" class="mr-2" :disabled="listLoading || items.length === 0" @click="downloadAsExcel">
                                    <fa icon="download" class="mr-1"/>엑셀 다운로드
                                </b-button>
                                <b-button size="sm" variant="primary" @click="openBulkUploadModal">
                                    <fa icon="upload" class="mr-1"/>벌크 등록
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <b-table
                        class="mb-0 sticky-header-sort-fix"
                        :items="items"
                        :fields="fields"
                        :busy="listLoading"
                        no-local-sorting
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        show-empty
                        hover
                        responsive="sm"
                        sticky-header="550px"
                        no-border-collapse
                        empty-html="<h6 class='mt-3 gray-400'>조회된 벤더코드가 없습니다.</h6>"
                        @sort-changed="onSortChanged"
                    >
                        <template #table-busy>
                            <div class="d-flex align-items-center justify-content-center my-3">
                                <b-spinner class="mr-2" variant="primary"/>
                                <span>로딩중...</span>
                            </div>
                        </template>
                        <template #cell(company)="{ value }">
                            {{ value && value.company_name || '-' }}
                        </template>
                        <template #cell(validated)="{ value }">
                            <b-badge :variant="value ? 'success' : 'light'">{{ value ? '검증됨' : '미검증' }}</b-badge>
                        </template>
                        <template #cell(validated_at)="{ value }">
                            {{ value ? value.slice(0, 10) : '-' }}
                        </template>
                    </b-table>
                    <div v-if="totalRows > perPage" class="d-flex justify-content-center py-3 border-top">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            :disabled="listLoading"
                            align="center"
                            class="mb-0"
                            @change="onPageChange"
                        />
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- 벌크 등록 모달 -->
        <VendorCodeBulkUploadModal modal-id="bulk-upload-modal" @submitted="onBulkSubmitted"/>

        <!-- 프로젝트 코드 검색 모달 -->
        <ProjectCodeLookupModal modal-id="project-code-lookup-modal" @selected="onProjectCodeSelected"/>
    </b-container>
</template>

<script>
import VendorCodeBulkUploadModal from './VendorCodeBulkUploadModal.vue';
import ProjectCodeLookupModal from './ProjectCodeLookupModal.vue';

export default {
    name: 'VendorCodeManagement',
    components: { VendorCodeBulkUploadModal, ProjectCodeLookupModal },
    data() {
        return {
            filter: {
                project_code: '',
                company_name: '',
                vendor_code: '',
                validated: null,
            },
            validatedOptions: [
                { value: null, text: '전체' },
                { value: true, text: '검증됨' },
                { value: false, text: '미검증' },
            ],
            items: [],
            listLoading: false,
            // 서버 페이지네이션/정렬 상태
            currentPage: 1,
            perPage: 50,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,
            fields: [
                { key: 'vendor_code', label: '벤더코드', sortable: true },
                { key: 'company', label: '업체명' },
                { key: 'project_code', label: '프로젝트 코드', sortable: true },
                { key: 'campaign_name', label: '캠페인' },
                { key: 'hd_corporate', label: 'HD법인' },
                { key: 'validated', label: '검증 여부', sortable: true },
                { key: 'validated_at', label: '검증일', sortable: true },
            ],
        };
    },
    methods: {
        async fetchList() {
            this.listLoading = true;
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                };
                if (this.filter.project_code) params.project_code = this.filter.project_code;
                if (this.filter.company_name) params.company_name = this.filter.company_name;
                if (this.filter.vendor_code) params.vendor_code = this.filter.vendor_code;
                if (this.filter.validated !== null) params.validated = this.filter.validated;
                if (this.sortBy) {
                    params.sort_by = this.sortBy;
                    params.sort_dir = this.sortDesc ? 'desc' : 'asc';
                }

                const { data } = await this.$axios.get('/vendor_codes', { params });
                this.items = data.items || [];
                this.totalRows = data.count || 0;
            } catch (e) {
                this.$bvToast.toast('조회 중 오류가 발생했습니다.', { title: '오류', variant: 'danger' });
            } finally {
                this.listLoading = false;
            }
        },
        // 검색 버튼: 항상 첫 페이지부터 조회
        onSearch() {
            this.currentPage = 1;
            this.fetchList();
        },
        // 페이지 이동 (b-pagination)
        onPageChange(page) {
            this.currentPage = page;
            this.fetchList();
        },
        // 서버 정렬 (컬럼 헤더 클릭) — 정렬 변경 시 첫 페이지부터
        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.currentPage = 1;
            this.fetchList();
        },
        resetFilter() {
            this.filter = { project_code: '', company_name: '', vendor_code: '', validated: null };
            this.items = [];
            this.totalRows = 0;
            this.currentPage = 1;
            this.sortBy = null;
            this.sortDesc = false;
        },
        downloadAsExcel() {
            const params = new URLSearchParams();
            if (this.filter.project_code) params.set('project_code', this.filter.project_code);
            if (this.filter.company_name) params.set('company_name', this.filter.company_name);
            if (this.filter.vendor_code) params.set('vendor_code', this.filter.vendor_code);
            if (this.filter.validated !== null) params.set('validated', this.filter.validated);
            params.set('as_file', 'true');
            window.open(`${process.env.VUE_APP_BACKENDHOST}/vendor_codes?${params.toString()}`, '_blank');
        },
        openBulkUploadModal() {
            this.$bvModal.show('bulk-upload-modal');
        },
        async onBulkSubmitted() {
            await this.fetchList();
        },
        openProjectCodeLookup() {
            this.$bvModal.show('project-code-lookup-modal');
        },
        onProjectCodeSelected({ project_code }) {
            this.filter.project_code = project_code;
        },
    },
};
</script>
